// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useParams } from '@tanstack/react-router';

// material ui icons
import DataUsageRoundedIcon from '@mui/icons-material/DataUsageRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';

// material ui
import { Alert, Stack } from '@mui/material';

// api
import type { ReadSensorDto } from '@/api/generated/recalc/endpoints/v1/sensors';
import { useGetSensorsByIdQuery } from '@/api/recalc/endpoints/sensorsApiSlice';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';
import LinearLoadingIndicator from '@/components/ui/progress/LinearLoadingIndicator';

// feature-components
import DeviceTabs from '@/features/devices/components/shared/DeviceTabs';
import SensorDetailHeader from '@/features/sensors/components/SensorDetailHeader';
import UnitSensorDataGrid from '@/features/sensors/components/UnitSensorDataGrid';

import { skipToken } from '@reduxjs/toolkit/query';

export const Route = createFileRoute('/$accountId/_user/sensors/$sensorId')({
  component: RouteComponent,
  staticData: { crumb: 'units-gateways-sensors:sensors.detailTitle' },
});

const SENSOR_TAB = {
  UNITS: 0,
  DATA: 1,
} as const;

type SensorTab = (typeof SENSOR_TAB)[keyof typeof SENSOR_TAB];

function RouteComponent() {
  const { t } = useTranslation('units-gateways-sensors');
  const { accountId, sensorId } = useParams({ strict: false });

  const [activeTab, setActiveTab] = useState<SensorTab>(SENSOR_TAB.UNITS);

  const parsedAccountId = accountId ? Number(accountId) : undefined;
  const parsedSensorId = sensorId ? Number(sensorId) : undefined;

  const hasValidAccountId =
    typeof parsedAccountId === 'number' && Number.isFinite(parsedAccountId);
  const hasValidSensorId =
    typeof parsedSensorId === 'number' && Number.isFinite(parsedSensorId);

  const querySensorId = hasValidSensorId ? parsedSensorId : skipToken;

  const {
    data: sensor,
    isLoading,
    isFetching,
    isError,
  } = useGetSensorsByIdQuery(querySensorId, {
    pollingInterval: 30000,
    skipPollingIfUnfocused: true,
  });

  const typedSensor = sensor as ReadSensorDto | undefined;

  const sensorAccountId = typedSensor?.Account?.AccountId
    ? Number(typedSensor.Account.AccountId)
    : undefined;
  const hasValidSensorAccountId =
    typeof sensorAccountId === 'number' && Number.isFinite(sensorAccountId);

  const isBusy = isLoading || isFetching;
  const isNotAccessible =
    !hasValidSensorId ||
    !hasValidAccountId ||
    isError ||
    (!isBusy && !typedSensor);
  const belongsToDifferentAccount =
    !isNotAccessible &&
    hasValidSensorAccountId &&
    hasValidAccountId &&
    sensorAccountId !== parsedAccountId;

  if (!isBusy && belongsToDifferentAccount) {
    return (
      <PageContainer maxWidth="xl" sx={{ py: 2 }}>
        <Alert
          severity="warning"
          sx={{ border: '1px solid', borderColor: 'warning.main' }}
          data-testid="account-mismatch-alert"
        >
          {t('sensors.accountMismatch', {
            defaultValue:
              'This sensor no longer belongs to the selected account.',
          })}
        </Alert>
      </PageContainer>
    );
  }

  if (!isBusy && isNotAccessible) {
    return (
      <PageContainer maxWidth="xl" sx={{ py: 2 }}>
        <Alert
          severity="error"
          sx={{ border: '1px solid', borderColor: 'error.main' }}
          data-testid="sensor-not-accessible-alert"
        >
          {t('sensors.notAccessible', {
            defaultValue:
              'This sensor is not accessible to your user or no longer exists.',
          })}
        </Alert>
      </PageContainer>
    );
  }

  return (
    <>
      <LinearLoadingIndicator isLoading={isBusy} />
      {!isBusy && typedSensor && (
        <PageContainer
          maxWidth="xl"
          sx={{ py: 2 }}
          data-testid="sensor-detail-container"
        >
          <PageHeader
            title={
              typedSensor
                ? `${typedSensor.SensorType} (${typedSensor.CalibrationType})`
                : ''
            }
          />
          <Stack spacing={2}>
            <SensorDetailHeader
              key={typedSensor?.SensorId}
              sensor={typedSensor}
            />

            <DeviceTabs
              activeTab={activeTab}
              onTabChange={(_, newValue) => {
                if (typeof newValue === 'number') {
                  setActiveTab(newValue as SensorTab);
                }
              }}
              tabs={[
                {
                  label: t('sensors.unitsTab', { defaultValue: 'Units' }),
                  testId: 'sensor-tab-units',
                  icon: <SensorsRoundedIcon fontSize="small" />,
                },
                {
                  label: t('sensors.dataTab', { defaultValue: 'Data' }),
                  testId: 'sensor-tab-data',
                  icon: <DataUsageRoundedIcon fontSize="small" />,
                },
              ]}
            />

            {activeTab === SENSOR_TAB.UNITS && typedSensor && (
              <UnitSensorDataGrid unitSensors={typedSensor.UnitSensors ?? []} />
            )}
            {activeTab === SENSOR_TAB.DATA && null}
          </Stack>
        </PageContainer>
      )}
    </>
  );
}
